import React, { useEffect, useMemo, useState } from 'react';
import { FolderKanban, ArrowLeft, Users, Target, Check, Star, ChevronDown, ChevronRight, CheckCircle2, Circle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PageShell } from '../components/PageShell';
import { Card, Badge, Avatar, Button } from '../components/primitives';
import { useDashTheme } from '../hooks/useDashTheme';
import { useGoalsSettings } from '../../../contexts/GoalsSettingsContext';
import * as goalsApi from '../../../services/goalsPerformanceApi';
import { computeProjectProgress, goalState } from '../../Goals/utils/projectProgress';

// Asana-style, read-only view of the current cycle's goals grouped by project.
// Everyone on a project sees the whole project's goals (same org-visibility
// precedent as the existing goals/by-project endpoint). Editing stays in
// My Goals / Team Goals.
const GoalsProjectsV2 = () => {
  const t = useDashTheme();
  const { settings: goalsSettings } = useGoalsSettings();
  const flowMode = goalsSettings?.goalsMode || 'classic';

  const [cycle, setCycle] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [collapsed, setCollapsed] = useState({}); // memberId -> bool (detail sections)

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cycleRes = await goalsApi.getCurrentCycle();
        const cycleData = Array.isArray(cycleRes?.data) ? cycleRes.data[0] : cycleRes?.data;
        if (cancelled || !cycleData?._id) { if (!cancelled) setLoading(false); return; }
        setCycle(cycleData);
        const res = await goalsApi.getProjectsSummary(cycleData._id);
        if (!cancelled && res?.success) setProjects(res.data?.projects || []);
      } catch (e) {
        console.error('Error loading projects summary:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Per-project derived data: members, progress, key
  const enriched = useMemo(() => projects.map(p => {
    const membersById = new Map();
    for (const g of p.goals) {
      const u = g.userId;
      if (u && typeof u === 'object' && u._id && !membersById.has(u._id)) membersById.set(u._id, u);
    }
    return {
      ...p,
      key: p.projectId?.toString() || 'none',
      members: [...membersById.values()],
      progress: computeProjectProgress(p.goals, flowMode),
      pendingCount: p.goals.filter(g => g.status === 'PENDING').length,
    };
  }).sort((a, b) => b.goals.length - a.goals.length), [projects, flowMode]);

  const selected = enriched.find(p => p.key === selectedKey) || null;

  const totals = useMemo(() => ({
    projects: enriched.length,
    people: new Set(enriched.flatMap(p => p.members.map(m => m._id))).size,
    goals: enriched.reduce((a, p) => a + p.goals.length, 0),
  }), [enriched]);

  const stateCell = (g) => {
    const s = goalState(g, flowMode);
    if (s.kind === 'done') {
      const Icon = (flowMode === 'star' || g.scoringMode === 'star5') ? Star : Check;
      return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: t.status.positive, fontSize: t.font.size.sm }}><Icon size={12} strokeWidth={2.5} /> {s.label}</span>;
    }
    if (s.kind === 'waiting') return <span style={{ color: t.status.warning, fontSize: t.font.size.sm }}>{s.label}</span>;
    if (s.kind === 'score') return <span style={{ color: t.c.text, fontWeight: t.font.weight.semibold, fontSize: t.font.size.sm, fontVariantNumeric: 'tabular-nums' }}>{s.label}</span>;
    return <span style={{ color: t.c.textTertiary, fontSize: t.font.size.sm }}>{s.label}</span>;
  };

  const AvatarStack = ({ members, size = 22 }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {members.slice(0, 4).map((m, i) => (
        <span key={m._id} style={{ marginLeft: i === 0 ? 0 : -6, borderRadius: 99, border: `2px solid ${t.c.surface}`, display: 'inline-flex' }}>
          <Avatar name={`${m.firstName || ''} ${m.lastName || ''}`.trim()} size={size} src={m.avatar} />
        </span>
      ))}
      {members.length > 4 && (
        <span style={{
          marginLeft: -6, width: size + 4, height: size + 4, borderRadius: 99,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: t.c.surfaceMuted, border: `2px solid ${t.c.surface}`,
          fontSize: 9, fontWeight: t.font.weight.semibold, color: t.c.textSecondary,
        }}>+{members.length - 4}</span>
      )}
    </div>
  );

  const ProgressBar = ({ pct }) => (
    <div style={{ height: 4, borderRadius: 99, background: t.c.surfaceMuted, overflow: 'hidden' }}>
      <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', borderRadius: 99, background: t.c.primary, transition: 'width 400ms ease' }} />
    </div>
  );

  return (
    <PageShell
      eyebrow="Performance · Goals"
      title="Projects"
      subtitle={cycle ? `What every project is working toward · ${cycle.month} ${cycle.year}` : 'What every project is working toward'}
      icon={<FolderKanban size={22} />}
    >
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: t.s[4] }}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="animate-pulse" style={{ height: 150, borderRadius: t.r.lg, background: t.c.surfaceMuted }} />
          ))}
        </div>
      ) : enriched.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: `${t.s[8]}px ${t.s[4]}px`, fontFamily: t.font.family }}>
            <FolderKanban size={28} style={{ color: t.c.textTertiary, marginBottom: t.s[3] }} />
            <div style={{ fontSize: t.font.size.lg, fontWeight: t.font.weight.medium, color: t.c.text }}>
              No project goals this cycle yet
            </div>
            <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, marginTop: 4, maxWidth: 380, margin: '4px auto 0' }}>
              Goals show up here once people add them and pick a project. Head to My Goals to add yours.
            </div>
          </div>
        </Card>
      ) : (
        <AnimatePresence mode="wait">
          {!selected ? (
            <motion.div key="grid" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
              {/* Cycle-wide summary strip */}
              <div style={{ display: 'flex', alignItems: 'center', gap: t.s[5], flexWrap: 'wrap', marginBottom: t.s[4], fontFamily: t.font.family }}>
                {[
                  { Icon: FolderKanban, label: 'Projects', value: totals.projects },
                  { Icon: Users, label: 'People', value: totals.people },
                  { Icon: Target, label: 'Goals', value: totals.goals },
                ].map(({ Icon, label, value }) => (
                  <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: t.s[2] }}>
                    <Icon size={14} style={{ color: t.c.textTertiary }} />
                    <span style={{ fontSize: t.font.size.base, fontWeight: t.font.weight.semibold, color: t.c.text, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                    <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary }}>{label}</span>
                  </span>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: t.s[4] }}>
                {enriched.map((p) => (
                  <Card key={p.key} interactive onClick={() => setSelectedKey(p.key)} style={{ cursor: 'pointer', fontFamily: t.font.family }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: t.s[3] }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: t.s[2] }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: t.font.size.md, fontWeight: t.font.weight.semibold, color: t.c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {p.projectName}
                          </div>
                          <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, marginTop: 2 }}>
                            {p.goals.length} goal{p.goals.length === 1 ? '' : 's'} · {p.members.length} {p.members.length === 1 ? 'person' : 'people'}
                          </div>
                        </div>
                        <AvatarStack members={p.members} />
                      </div>

                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>
                            {p.progress.done}/{p.progress.total} {p.progress.doneLabel}
                          </span>
                          <span style={{ fontSize: t.font.size.xs, fontWeight: t.font.weight.semibold, color: t.c.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
                            {p.progress.donePct}%
                          </span>
                        </div>
                        <ProgressBar pct={p.progress.donePct} />
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: t.s[2], flexWrap: 'wrap' }}>
                        {p.pendingCount > 0
                          ? <Badge tone="warning">{p.pendingCount} awaiting approval</Badge>
                          : <Badge tone="positive" dot>All approved</Badge>}
                        {flowMode === 'score_only' && p.progress.avgScore != null && (
                          <Badge tone="brand">Avg {p.progress.avgScore}/10</Badge>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div key="detail" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: t.s[3], marginBottom: t.s[4], fontFamily: t.font.family }}>
                <Button variant="secondary" size="sm" leftIcon={<ArrowLeft size={14} />} onClick={() => setSelectedKey(null)}>
                  All projects
                </Button>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: t.font.size.lg, fontWeight: t.font.weight.semibold, color: t.c.text }}>{selected.projectName}</div>
                  <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary }}>
                    {selected.goals.length} goals · {selected.members.length} {selected.members.length === 1 ? 'person' : 'people'} · {selected.progress.done}/{selected.progress.total} {selected.progress.doneLabel}
                  </div>
                </div>
              </div>

              {/* Asana-style list: column header + collapsible member sections */}
              {(() => {
                const GRID = 'minmax(260px, 1fr) 170px 120px 70px 110px 130px';
                const isStarWeights = flowMode === 'star';
                const cellBase = { fontSize: t.font.size.sm, color: t.c.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
                return (
                  <Card padding={0} style={{ overflowX: 'auto', fontFamily: t.font.family }}>
                    <div style={{ minWidth: 880 }}>
                      {/* Column header */}
                      <div style={{
                        display: 'grid', gridTemplateColumns: GRID, alignItems: 'center',
                        padding: `${t.s[2]}px ${t.s[4]}px`, borderBottom: `1px solid ${t.c.border}`,
                      }}>
                        {['Name', 'Assignee', 'Target', isStarWeights ? 'Points' : 'Weight', 'Status', 'Progress'].map(h => (
                          <span key={h} style={{ fontSize: t.font.size.xs, fontWeight: t.font.weight.medium, color: t.c.textTertiary, letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
                            {h}
                          </span>
                        ))}
                      </div>

                      {selected.members.map((m) => {
                        const memberGoals = selected.goals.filter(g => g.userId?._id === m._id);
                        const mp = computeProjectProgress(memberGoals, flowMode);
                        const memberName = `${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email;
                        const isCollapsed = !!collapsed[m._id];
                        const Caret = isCollapsed ? ChevronRight : ChevronDown;
                        return (
                          <React.Fragment key={m._id}>
                            {/* Section header — like Asana's sprint groups */}
                            <div
                              onClick={() => setCollapsed(c => ({ ...c, [m._id]: !c[m._id] }))}
                              style={{
                                display: 'flex', alignItems: 'center', gap: t.s[2], cursor: 'pointer',
                                padding: `${t.s[2]}px ${t.s[4]}px`, background: t.c.surfaceMuted,
                                borderBottom: `1px solid ${t.c.border}`,
                              }}
                            >
                              <Caret size={14} style={{ color: t.c.textTertiary, flexShrink: 0 }} />
                              <Avatar name={memberName} size={18} src={m.avatar} />
                              <span style={{ fontSize: t.font.size.base, fontWeight: t.font.weight.semibold, color: t.c.text }}>
                                {memberName}
                              </span>
                              {m.position && (
                                <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>· {m.position}</span>
                              )}
                              <span style={{ marginLeft: 'auto', fontSize: t.font.size.xs, color: t.c.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
                                {mp.done}/{mp.total} {mp.doneLabel}
                              </span>
                            </div>

                            {/* Rows */}
                            {!isCollapsed && memberGoals.map((g) => {
                              const s = goalState(g, flowMode);
                              const isDone = s.kind === 'done';
                              return (
                                <div
                                  key={g._id}
                                  style={{
                                    display: 'grid', gridTemplateColumns: GRID, alignItems: 'center',
                                    padding: `7px ${t.s[4]}px`, borderBottom: `1px solid ${t.c.border}40`,
                                    transition: 'background 120ms',
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = t.c.surfaceHover; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                >
                                  {/* Name + check circle + critical chip */}
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: t.s[2], minWidth: 0, paddingRight: t.s[3] }}>
                                    {isDone
                                      ? <CheckCircle2 size={15} style={{ color: t.status.positive, flexShrink: 0 }} />
                                      : <Circle size={15} style={{ color: t.c.textTertiary, flexShrink: 0 }} />}
                                    <span style={{ fontSize: t.font.size.base, color: t.c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {g.name}
                                    </span>
                                    {g.critical && <Badge tone="negative">Critical</Badge>}
                                  </span>
                                  {/* Assignee */}
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                    <Avatar name={memberName} size={18} src={m.avatar} />
                                    <span style={cellBase}>{memberName}</span>
                                  </span>
                                  {/* Target */}
                                  <span style={{ ...cellBase, fontVariantNumeric: 'tabular-nums' }}>{g.metric || '—'}</span>
                                  {/* Weight / Points */}
                                  <span style={{ ...cellBase, fontVariantNumeric: 'tabular-nums' }}>
                                    {g.weightage != null ? `${g.weightage}${isStarWeights || g.scoringMode === 'star5' ? ' pts' : '%'}` : '—'}
                                  </span>
                                  {/* Status chip — Asana priority-chip feel */}
                                  <span>
                                    {g.status === 'APPROVED'
                                      ? <Badge tone="positive">Approved</Badge>
                                      : <Badge tone="warning">Pending</Badge>}
                                  </span>
                                  {/* Progress / score */}
                                  <span style={{ whiteSpace: 'nowrap' }}>{stateCell(g)}</span>
                                </div>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </Card>
                );
              })()}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </PageShell>
  );
};

export default GoalsProjectsV2;
